import { cloneElement, type ReactElement, type MouseEvent } from "react";
import { ConfirmDialog, ConfirmDialogProps } from "../confirm-dialog";
import { i18n } from "@tellescope/i18n";
import { useUncontrolledState } from "../../hooks";

export interface DestructiveConfirmDialogProps extends Omit<ConfirmDialogProps, "variant" | "onConfirm"> {
  /**
   * The button/trigger element that opens the dialog when clicked.
   * Should be a clickable element like a button.
   */
  children?: ReactElement<any>;
  /**
   * Callback executed when the user confirms the destructive action.
   * The dialog will close automatically after this completes.
   */
  onConfirm: () => void | Promise<void>;
}

/**
 * A wrapper component that renders a child button/trigger and opens a destructive
 * confirmation dialog when clicked. The dialog will execute the onConfirm action
 * when the user confirms.
 *
 * @example
 * ```tsx
 * <DestructiveConfirmDialog
 *   title="Delete Account"
 *   description="This will permanently delete your account and all associated data."
 *   confirmText="Delete Account"
 *   onConfirm={async () => {
 *     await deleteAccount();
 *   }}
 * >
 *   <Button variant="destructive">Delete Account</Button>
 * </DestructiveConfirmDialog>
 * ```
 */
export const DestructiveConfirmDialog = ({
  children,
  onConfirm,
  title = i18n.t("Are you sure?", { ns: "ui" }),
  description = i18n.t("This will permanently delete the item. This action cannot be undone.", { ns: "ui" }),
  confirmText = i18n.t("Delete", { ns: "ui" }),
  cancelText = i18n.t("Cancel", { ns: "ui" }),
  open: providedOpen,
  onOpenChange: providedOnOpenChange,
  ...dialogProps
}: DestructiveConfirmDialogProps) => {
  const [open, setOpen] = useUncontrolledState({
    state: providedOpen ?? false,
    setState: providedOnOpenChange
      ? (newVal) => {
          const currentVal = providedOpen ?? false;
          const result = typeof newVal === "function" ? newVal(currentVal) : newVal;
          providedOnOpenChange(result);
          return result;
        }
      : undefined,
  });

  // Clone the child element and add onClick handler
  const trigger = children
    ? cloneElement(children, {
        onClick: (event: MouseEvent) => {
          event.preventDefault();
          event.stopPropagation();
          // Call original onClick if it exists
          children.props.onClick?.(event);
          // Open the dialog
          setOpen(true);
        },
      })
    : undefined;

  return (
    <>
      {trigger}
      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        variant="destructive"
        title={title}
        description={description}
        confirmText={confirmText}
        cancelText={cancelText}
        onConfirm={onConfirm}
        onCancel={(event) => {
          // Stop propagation when dialog is cancelled to prevent triggering parent click handlers
          event?.preventDefault();
          event?.stopPropagation();
        }}
        {...dialogProps}
      />
    </>
  );
};
